import { ThemedText } from "@/components/themed-text";
import { SUPPORT_MAIL, SUPPORT_PHONE } from "@/constants";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { showAlert } from "@/lib/alert";
import Ionicons from "@react-native-vector-icons/ionicons";
import * as Location from "expo-location";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import {
  Linking,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import panicService from "../lib/services/panic";

export default function PanicScreen() {
  const router = useRouter();
  const { bookingId } = useLocalSearchParams<{ bookingId: string }>();
  const { theme } = useAuth();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmergency = async () => {
    if (!bookingId) {
      showAlert("Error", "Booking ID is missing");
      return;
    }

    if (!reason) {
      showAlert("Error", "Please provide a reason");
      return;
    }

    showAlert(
      "Emergency Alert",
      "Are you sure you want to trigger an emergency alert? Emergency services will be notified.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Yes, Send Alert",
          style: "destructive",
          onPress: sendPanicAlert,
        },
      ],
    );
  };

  const sendPanicAlert = async () => {
    try {
      setLoading(true);

      // Get current location
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== "granted") {
        showAlert(
          "Error",
          "Location permission is required for emergency alerts",
        );
        return;
      }

      const location = await Location.getCurrentPositionAsync({});

      await panicService.reportPanic({
        booking_id: Number(bookingId),
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        reason: reason || undefined,
      });

      showAlert(
        "Emergency Alert Sent",
        "Help is on the way. Stay calm and safe.",
        [
          {
            text: "OK",
            onPress: () => router.back(),
          },
        ],
      );
    } catch (error: any) {
      showAlert(
        "Error",
        error.response?.data?.message || "Failed to send emergency alert",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: "Emergency",
          headerShown: true,
          headerStyle: {
            backgroundColor: theme.background,
          },
          headerTintColor: theme.text,
          headerBackTitle: "Back",
        }}
      />
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.background }]}
        edges={["bottom"]}
      >
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.warningCard}>
            <Ionicons name="alert-circle" size={64} color={BrandColors.error} />
            <ThemedText style={styles.warningTitle} type="subtitle">
              Emergency Assistance
            </ThemedText>
            <ThemedText style={styles.warningText} type="small">
              Press the button below if you need immediate help. Your location
              will be shared with emergency services and our support team.
            </ThemedText>
          </View>

          <View style={styles.section}>
            <ThemedText style={styles.label} type="defaultSemiBold">
              Describe the situation (optional)
            </ThemedText>
            <TextInput
              style={styles.textArea}
              placeholder="What's the emergency?"
              placeholderTextColor={BrandColors.mediumGray}
              value={reason}
              onChangeText={setReason}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <TouchableOpacity
            style={[styles.panicButton, loading && styles.panicButtonDisabled]}
            onPress={handleEmergency}
            disabled={loading}
          >
            <Ionicons name="alert-circle" size={32} color="#fff" />
            <ThemedText style={styles.panicButtonText} type="defaultSemiBold">
              {loading ? "SENDING ALERT..." : "SEND EMERGENCY ALERT"}
            </ThemedText>
          </TouchableOpacity>

          <View style={styles.infoCard}>
            <Ionicons
              name="information-circle"
              size={24}
              color={BrandColors.secondary}
            />
            <ThemedText style={styles.infoText} type="extraSmall">
              This feature should only be used in genuine emergencies. Misuse
              may result in account suspension.
            </ThemedText>
          </View>

          <View style={styles.emergencyContacts}>
            <ThemedText style={styles.contactsTitle} type="defaultSemiBold">
              Emergency Contacts
            </ThemedText>
            <TouchableOpacity
              style={[
                styles.contactButton,
                { backgroundColor: theme.background },
              ]}
              onPress={async () =>
                await Linking.openURL(`tel:${SUPPORT_PHONE}`)
              }
            >
              <Ionicons name="call" size={24} color={BrandColors.success} />
              <ThemedText style={styles.contactText} type="smallSemiBold">
                Call Emergency Services
              </ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.contactButton,
                { backgroundColor: theme.background },
              ]}
              onPress={async () =>
                await Linking.openURL(`mailto:${SUPPORT_MAIL}`)
              }
            >
              <Ionicons
                name="chatbubbles"
                size={24}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.contactText} type="smallSemiBold">
                Contact Support
              </ThemedText>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
    padding: 20,
    paddingTop: 0,
  },
  warningCard: {
    backgroundColor: "#fff",
    borderRadius: 24,
    padding: 32,
    alignItems: "center",
    borderWidth: 2,
    borderColor: BrandColors.error,
    shadowColor: BrandColors.error,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
  },
  warningTitle: {
    fontSize: 24,
    color: BrandColors.error,
    marginTop: 20,
    marginBottom: 12,
    letterSpacing: -0.5,
  },
  warningText: {
    fontSize: 16,
    color: BrandColors.darkGray,
    textAlign: "center",
    lineHeight: 24,
  },
  section: {
    marginTop: 32,
  },
  label: {
    fontSize: 16,
    marginBottom: 12,
    marginLeft: 4,
  },
  textArea: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    borderRadius: 16,
    padding: 16,
    fontSize: 16,
    height: 120,
    textAlignVertical: "top",
  },
  panicButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.error,
    borderRadius: 20,
    padding: 24,
    marginTop: 32,
    gap: 12,
    shadowColor: BrandColors.error,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 12,
  },
  panicButtonDisabled: {
    backgroundColor: BrandColors.mediumGray,
    shadowOpacity: 0.1,
  },
  panicButtonText: {
    color: "#fff",
    fontSize: 20,
    fontWeight: "800",
    letterSpacing: 1,
  },
  infoCard: {
    flexDirection: "row",
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 16,
    padding: 16,
    marginTop: 32,
    gap: 12,
    borderWidth: 1,
    borderColor: BrandColors.primary + "20",
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: BrandColors.secondary,
    lineHeight: 20,
    fontWeight: "500",
  },
  emergencyContacts: {
    marginTop: 32,
  },
  contactsTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 16,
    marginLeft: 4,
  },
  contactButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    gap: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  contactText: {
    fontSize: 16,
    fontWeight: "600",
  },
});
